/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module avi/core
 */

import {
    IDryrunInfo,
    IEventMap,
    IMustChecksInfo,
    ISeUpgradeEvents,
    IUpgradeReadinessCheckObj,
    IUpgradeStatusInfo,
    NodeType,
    UpgradeFsmState,
} from 'generated-types';

import { Item } from 'ajs/modules/data-model/factories/item.factory';

import {
    IUpgradeNodeVersionHash,
    UpgradeNodeType,
    UpgradePreChecksStatusTypeHash,
    UpgradeState,
    UpgradeStateGroup,
} from 'ng/modules/update/update.types';

import { UpdateService } from 'ng/modules/update/services/update.service';
import { Constructor } from '../../../declarations/globals.d';
import { UPDATE_SERVICE_TOKEN } from '../../../downgrade-services.tokens';

export interface IUpgradeStatusEssential {
    getUpgradeStatusConfig(): IUpgradeStatusInfo;
    getCloudRef(): string;
}

/**
 * @description
 *     Mixin to extend a BaseClass with methods for feteching upgrade status.
 * @author Zhiqian Liu
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, require-jsdoc
export function withUpgradeStatusMixin<T extends Constructor<Item<any>>>(BaseClass: T) {
    return class UpgradeStatusItem extends BaseClass implements IUpgradeStatusEssential {
        public static ajsDependencies = [
            UPDATE_SERVICE_TOKEN,
        ];
        public upgradeStateToStateGroupMap_: Map<typeof UpgradeState, UpgradeStateGroup>;
        public getUpgradeStatusConfig: () => IUpgradeStatusInfo;
        public getCloudRef: () => string;

        /**
         * @override
         */
        constructor(...args: any[]) {
            super(...args);

            const updateService: UpdateService = this.getAjsDependency_(UPDATE_SERVICE_TOKEN);

            this.upgradeStateToStateGroupMap_ = updateService.upgradeStateToStateGroupMap;
        }

        /**
         * Getter for the complete hash of all version info from the node.
         */
        protected get versionAsHash(): IUpgradeNodeVersionHash {
            const {
                version,
                patch_version: patchVersion,
            } = this.getUpgradeStatusConfig();

            return UpdateService.getVersionAsHash(version, patchVersion);
        }

        /**
         * Getter for the complete hash of all previous version info from the node.
         */
        protected get previousVersionAsHash(): IUpgradeNodeVersionHash {
            const {
                previous_version: previousVersion,
                previous_patch_version: previousPatchVersion,
            } = this.getUpgradeStatusConfig();

            return UpdateService.getVersionAsHash(previousVersion, previousPatchVersion);
        }

        /**
         * Getter for the major version of this node.
         * Example formats of major versions: '18.2.8', '20.1.1'.
         * @return {string}
         */
        public get majorVersion(): string {
            const { version } = this.versionAsHash;

            return version;
        }

        /**
         * Getter for the patch version of this node. Consisting of major patch version and minor
         * patch version.
         * Example format of patch versions (<major>p<minor>): '2p1', '3p2'.
         */
        public get patchVersion(): string {
            const { patchVersion } = this.versionAsHash;

            return patchVersion || '';
        }

        /**
         * Getter for the major patch version of this node.
         * If there exists patch version '2p1', then the '2' before the 'p' will be returned.
         */
        public get majorPatchVersion(): string {
            const { majorPatchVersion } = this.versionAsHash;

            return majorPatchVersion || '';
        }

        /**
         * Getter for the minor patch version of this node.
         * If there exists patch version '2p1', then the '1' after the 'p' will be returned.
         */
        public get minorPatchVersion(): string {
            const { minorPatchVersion } = this.versionAsHash;

            return minorPatchVersion || '';
        }

        /**
         * Getter for the previous major version of this node. This version won't exist if the
         * current version is not upgraded from an old version.
         * Expample formats of major versions: '18.2.8', '20.1.1'.
         */
        public get previousMajorVersion(): string {
            const { version } = this.previousVersionAsHash;

            return version || '';
        }

        /**
         * Getter for the previous patch version of this node. This version won't exist if there was
         * no patch applied.
         * Example formats of patch versions will be as '2p1', '3p2'.
         */
        public get previousPatchVersion(): string {
            const { patchVersion } = this.previousVersionAsHash;

            return patchVersion || '';
        }

        /**
         * Getter for type of the node.
         */
        public get nodeType(): NodeType {
            return this.getUpgradeStatusConfig().node_type;
        }

        /**
         * Getter for the latest upgrade starting time of the node.
         */
        public get upgradeStartTime(): string {
            const { start_time: startTime } = this.getUpgradeStatusConfig();

            return startTime ?? '';
        }

        /**
         * Return the Dryrun operation start time.
         */
        public get dryrunOperationStartTime(): string {
            const { dryrun_info: dryrunInfo = {} } = this.getUpgradeStatusConfig();

            return dryrunInfo?.start_time ?? '';
        }

        /**
         * Return the Dryrun operation end time.
         */
        public get dryrunOperationEndTime(): string {
            const { dryrun_info: dryrunInfo = {} } = this.getUpgradeStatusConfig();

            return dryrunInfo?.end_time ?? '';
        }

        /**
         * Getter for the upgrade operation.
         */
        public get upgradeOps(): string {
            const { upgrade_ops: upgradeOps }: { upgrade_ops?: string } =
                this.getUpgradeStatusConfig();

            return upgradeOps && this.stringService.enumeration(upgradeOps) || '';
        }

        /**
         * Return the operation type for Dryrun flow.
         */
        public getDryrunOperationType(): string {
            const { dryrun_info: dryrunInfo = {} } = this.getUpgradeStatusConfig();

            return dryrunInfo?.operation &&
                this.stringService.enumeration(dryrunInfo.operation) || '';
        }

        /**
         * Getter for cloud name of the node; applies to non-controller node only.
         */
        public get cloudName(): string {
            if (this.isNodeType(UpgradeNodeType.CONTROLLER)) {
                throw new Error(
                    `Can't fetch cloud name for node of type ${UpgradeNodeType.CONTROLLER}`,
                );
            }

            const cloudRef = this.getCloudRef();

            return this.stringService.name(cloudRef);
        }

        /**
         * Getter for the latest upgrade end time of the node.
         */
        public get upgradeEndTime(): string {
            const { end_time: endTime } = this.getUpgradeStatusConfig();

            // Tell interpreter that time is in GMT/UTC, remove if/when API returns timezone:
            return endTime ?? '';
        }

        /**
         * Getter for upgrade progress percentage.
         */
        public get progressPercentage(): number {
            const { progress } = this.getUpgradeStatusConfig();

            return typeof progress === 'number' ? progress : NaN;
        }

        /**
         * Getter for upgrade readiness config.
         */
        public get upgradeReadiness(): IUpgradeReadinessCheckObj {
            const { upgrade_readiness: upgradeReadiness } = this.getUpgradeStatusConfig();

            return upgradeReadiness;
        }

        /**
         * Return the dryrun info object.
         */
        public get dryrunInfo(): IDryrunInfo {
            return this.getUpgradeStatusConfig()?.dryrun_info;
        }

        /**
         * Return the dryrun progress percentage.
         */
        public get dryrunProgressPercentage(): number {
            const { progress } = this.dryrunInfo;

            return !Number.isNaN(progress) ? progress : NaN;
        }

        /**
         * Getter for pre checks list from upgrade readiness config.
         */
        public get precheckList(): IMustChecksInfo[] {
            const { checks = [] } = this.upgradeReadiness;

            return checks;
        }

        /**
         * Return formatted transcript of the node config.
         */
        public get transcript(): string {
            return JSON.stringify(this.getUpgradeStatusConfig(), null, 4);
        }

        /**
         * Getter for controller/se-group upgrade events list.
         */
        public get upgradeEvents(): IEventMap[] | ISeUpgradeEvents[] {
            const isControllerNode = this.isNodeType(UpgradeNodeType.CONTROLLER);

            const {
                upgrade_events: upgradeEvents,
                se_upgrade_events: seUpgradeEvents,
            } = this.getUpgradeStatusConfig();

            return isControllerNode ? upgradeEvents : seUpgradeEvents;
        }

        /**
         * Return Controller Dryrun Upgrade Events.
         */
        public get dryrunUpgradeEvents(): IEventMap[] {
            const { dryrun_info: dryrunInfo } = this.getUpgradeStatusConfig();

            return dryrunInfo.upgrade_events;
        }

        /**
         * Getter for upgrade state of this node.
         */
        public get state(): UpgradeFsmState {
            const { state: { state: upgradeState } = {} } = this.getUpgradeStatusConfig();

            return upgradeState;
        }

        /**
         * Return precheck state of upgrade readiness object.
         */
        public get upgradeReadinessState(): UpgradeFsmState {
            const { state: { state: upgradeState } = {} } = this.upgradeReadiness;

            return upgradeState;
        }

        /**
         * Getter for upgrade state group of this node.
         */
        public get stateGroup(): UpgradeStateGroup {
            return this.upgradeStateToStateGroupMap_[this.state];
        }

        /**
         * Return the state group of the node's dry run state.
         */
        public get dryrunStateGroup(): UpgradeStateGroup {
            const { dryrun_info: dryrunInfo = {} } = this.getUpgradeStatusConfig();

            return this.upgradeStateToStateGroupMap_[dryrunInfo.state.state];
        }

        /**
         * Get current version of this node.
         * Example returned string format: (<majorVersion>-<patchVersion>): '20.1.1-2p1'.
         * When withBuild is true, expected returned string will be as format
         * <majorVersion>-<patchVersion>-<build>, ie. '20.1.1-2p1-5038'.
         * @param [withBuild=false] Whether to include build number.
         */
        public getVersion(withBuild = false): string {
            return UpdateService.generateCompleteVersionFromHash(this.versionAsHash, withBuild);
        }

        /**
         * Return the current version of this node without patch version.
         * Example returned string format: '20.1.1'.
         * When withBuild is true, expected returned string will be as format
         * <majorVersion>-<build>, ie. '20.1.1-5038'.
         * @param [withBuild=false] Whether to include build number.
         */
        public getVersionWithoutPatch(withBuild = false): string {
            const { version, build } = this.versionAsHash;

            if (withBuild && build) {
                return `${version}-${build}`;
            }

            return version || '';
        }

        /**
         * Get previous version of this node. Unlike major version and patch version, previous major
         * version and previous patch version are independent. There can exist previous patch
         * version but not previous major version. Therefore the latter must be there for the whole
         * string.
         * Example returned version string (<majorVersion>-<patchVersion>): '18.2.8-3p1'.
         * When withBuild is true (<majorVersion>-<patchVersion>-<build>): '18.2.8-3p1-2046'.
         * @param [withBuild=false] - Whether to include build number.
         */
        public getPreviousVersion(withBuild = false): string {
            const { version } = this.previousVersionAsHash;

            if (version) {
                return UpdateService.generateCompleteVersionFromHash(
                    this.previousVersionAsHash,
                    withBuild,
                );
            }

            return '';
        }

        /**
         * Decide is this node of a specific type.
         */
        public isNodeType(type: string): boolean {
            return this.nodeType === type;
        }

        /**
         * Decide is this node in initial state and hasn't gone through any upgrade operations.
         */
        public isInInitState(): boolean {
            const { stateGroup } = this;

            return stateGroup === UpgradeStateGroup.GROUP_INIT;
        }

        /**
         * Decide is this node in upgrade progress.
         */
        public isUpgradeInProgress(): boolean {
            const { stateGroup } = this;

            return stateGroup === UpgradeStateGroup.GROUP_IN_PROGRESS;
        }

        /**
         * Return if Dryrun is in progress.
         */
        public isDryrunInProgress(): boolean {
            const { dryrunInfo } = this;

            return dryrunInfo?.state?.state === UpgradeFsmState.UPGRADE_FSM_IN_PROGRESS;
        }

        public arePrechecksForNodeInProgress() : boolean {
            const {
                UPGRADE_PRE_CHECK_IN_PROGRESS,
                UPGRADE_PRE_CHECK_STARTED,
            } = UpgradePreChecksStatusTypeHash;
            const { upgrade_readiness: upgradeReadiness = {} } = this.getUpgradeStatusConfig();
            const { state } = upgradeReadiness;

            return state?.state === UPGRADE_PRE_CHECK_IN_PROGRESS ||
                state?.state === UPGRADE_PRE_CHECK_STARTED;
        }

        /**
         * Return the image_ref version for prechecks.
         */
        public getImageRefVersion(withBuild = false): string {
            const { upgrade_readiness: upgradeReadiness = {} } = this.getUpgradeStatusConfig();
            const {
                image_ref: imageRef,
                patch_image_ref: patchImageRef,
            } = upgradeReadiness;

            let version = imageRef ? this.stringService.name(imageRef) : '';
            const patchVersionString = patchImageRef ? this.stringService.name(patchImageRef) : '';

            const patchVersionArray = patchVersionString.split('-');
            const patchVersion = patchVersionArray?.length > 2 ? patchVersionArray[2] : '';

            /**
             * In case of patch upgrade, we won't have version available. So, we need to curate
             * it by using the splitted elements from patch_image_ref.
             */
            if (patchVersion && !version) {
                version = `${patchVersionArray[0]}-${patchVersionArray[1]}-${patchVersionArray[3]}`;
            }

            const versionHash = UpdateService.getVersionAsHash(version, patchVersion);

            return UpdateService.generateCompleteVersionFromHash(versionHash, withBuild);
        }

        /**
         * Return the image_ref version for Dryrun.
         */
        public getDryrunImageRefVersion(withBuild = false): string {
            const { params } = this.getUpgradeStatusConfig()?.dryrun_info || {};

            const version = params?.image_ref ? this.stringService.name(params.image_ref) : '';
            const versionHash = UpdateService.getVersionAsHash(version);

            return UpdateService.generateCompleteVersionFromHash(versionHash);
        }

        /**
         * Return if prechecks failed.
         */
        public preChecksFailed(): boolean {
            const { upgrade_readiness: upgradeReadiness = {} } = this.getUpgradeStatusConfig();
            const { state = {} } = upgradeReadiness;
            const { UPGRADE_PRE_CHECK_ERROR } = UpgradePreChecksStatusTypeHash;

            return state.state === UPGRADE_PRE_CHECK_ERROR;
        }

        /**
         * Return if pre-checks completed with warning.
         */
        public preChecksCompletedWithWarning(): boolean {
            const { upgrade_readiness: upgradeReadiness = {} } = this.getUpgradeStatusConfig();
            const { state = {} } = upgradeReadiness;
            const { UPGRADE_PRE_CHECK_WARNING } = UpgradePreChecksStatusTypeHash;

            return state.state === UPGRADE_PRE_CHECK_WARNING;
        }

        /**
         * Return the upgrade ops value for prechecks.
         */
        public get preChecksUpgradeOps(): string {
            const { upgrade_readiness: upgradeReadiness = {} } = this.getUpgradeStatusConfig();
            const { upgrade_ops: upgradeOps } = upgradeReadiness;

            return upgradeOps && this.stringService.enumeration(upgradeOps) || '';
        }

        /**
         * Return the latest pre-checks starting time of the node.
         */
        public get preChecksStartTimeStamp(): string {
            const { upgrade_readiness: upgradeReadiness = {} } = this.getUpgradeStatusConfig();
            const { start_time: startTime } = upgradeReadiness;

            // Tell interpreter that time is in GMT/UTC.
            return startTime ?? '';
        }

        /**
         * Return the image_ref version for Upgrade failure.
         */
        public getUpgradeFailureImageRefVersion(): string {
            const { upgrade_readiness: upgradeReadiness = {} } = this.getUpgradeStatusConfig();
            const version = upgradeReadiness?.image_ref ?
                this.stringService.name(upgradeReadiness.image_ref) : '';
            const versionHash = UpdateService.getVersionAsHash(version);

            return UpdateService.generateCompleteVersionFromHash(versionHash);
        }
    };
}
